import { useEffect, useRef, useState } from "react";
import TemplatePreviewFrame from "./TemplatePreviewFrame";
import "./_scaled-template-preview.scss";

// Matches --doc-page-width in _scaled-template-preview.scss (A4 at 96dpi) -
// the real, unscaled width TemplatePreviewFrame renders its iframe at.
const DOC_PAGE_WIDTH = 794;

/**
 * Wraps TemplatePreviewFrame with "fit the pane's actual width" scaling -
 * shared by every wizard's live document preview (Proposals, Invoices,
 * Upsells/Downsells) so the ResizeObserver plumbing exists exactly once.
 * Extracted from _features/proposals/components/ProposalPreviewPane.jsx,
 * where this first shipped - see that file's git history for the original,
 * single-consumer version.
 *
 * A host's own preview pane column is rarely exactly 794px (a real A4 page)
 * wide, and can be narrower OR wider depending on viewport/layout - rather
 * than rendering at that fixed width and clipping/scrolling whatever
 * doesn't fit, this measures its own content width (`wrapRef`) and scales
 * the whole page uniformly (`scaler-inner`'s CSS transform) to match it
 * exactly. `contentRef` separately measures the page's own natural
 * (pre-transform) height, since TemplatePreviewFrame's height is content-
 * dependent and ResizeObserver's contentRect ignores CSS transforms - both
 * measurements are needed to size the outer box so the scaled page doesn't
 * leave layout gaps (transform alone doesn't shrink the space an element
 * reserves).
 */
const ScaledTemplatePreview = ({ srcDoc, orientation = "portrait" }) => {
  const wrapRef = useRef(null);
  const contentRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [naturalHeight, setNaturalHeight] = useState(0);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return undefined;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect?.width;
      if (width > 0) setScale(width / DOC_PAGE_WIDTH);
    });
    observer.observe(wrap);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const content = contentRef.current;
    if (!content) return undefined;
    const observer = new ResizeObserver((entries) => {
      const height = entries[0]?.contentRect?.height;
      if (height > 0) setNaturalHeight(height);
    });
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="scaled-template-preview__frame-wrap" ref={wrapRef}>
      <div
        className="scaled-template-preview__scaler"
        style={{
          width: DOC_PAGE_WIDTH * scale,
          height: naturalHeight ? naturalHeight * scale : undefined,
        }}
      >
        <div
          ref={contentRef}
          className="scaled-template-preview__scaler-inner"
          style={{ width: DOC_PAGE_WIDTH, transform: `scale(${scale})` }}
        >
          <TemplatePreviewFrame srcDoc={srcDoc} orientation={orientation} />
        </div>
      </div>
    </div>
  );
};

export default ScaledTemplatePreview;
